import { distance as turfDistance } from '@turf/turf';
import { ManeuverSign, isTerminal } from './navigation/maneuverSign';

/**
 * One turn on a snapped segment, positioned by metres from that segment's start.
 *
 * This is the stored shape, shared with iOS and Android — the recording apps read these
 * straight out of the hike and guide from them with no network and no map-matching.
 *
 * Distance rather than GraphHopper's `interval` index, because an index does not survive
 * the planner: bridging prepends a point, marker insertion splits a segment, reversal flips
 * it. A distance survives all three, and the recording needs a distance anyway.
 */
export interface TurnInstruction {
  d: number;
  sign: number;
  text: string;
  street?: string;
  /**
   * Set only on a cue the planner added by hand: "turn" for one with a direction, "note" for
   * one without.
   *
   * It exists because the recording apps discard GraphHopper's `text` for every sign they can
   * word themselves, and recompose the sentence from the sign and the street. That is right
   * for a routed turn and wrong for a cue, whose whole point is the words the planner typed.
   * The key is what tells them apart.
   */
  cue?: CueKind;
  /**
   * What a person wrote for this turn, if anyone did — for a cue they added and for a routed
   * turn they re-worded alike. GraphHopper's own `text` is never overwritten, so clearing this
   * key restores the original sentence for free, whatever its sign.
   */
  utext?: string;
  /**
   * The direction a person chose, overriding the one GraphHopper reported. Separate from
   * `utext` because reversing keeps a mirrored sign and drops re-worded text.
   */
  usign?: number;
  /**
   * Suppressed: kept in storage so the route still knows the turn is there, but not shown,
   * not spoken and not counted down to. A routed turn is flagged rather than removed, because
   * the next re-snap would hand it straight back.
   */
  hidden?: boolean;
  /**
   * From GraphHopper routing straight through a marker rather than up to it. On the bookends
   * either side of a marker it says the marker is settled, turn or no turn, so nothing is
   * inferred there; readers that predate it drop both bookends, so they keep inferring.
   */
  src?: typeof JOIN_SOURCE;
}

export const JOIN_SOURCE = 'join';

export function isJoinEntry(turn: TurnInstruction): boolean {
  return turn.src === JOIN_SOURCE;
}

/**
 * The profile of a segment drawn as a straight line, the same value the phones write. Only
 * this marks a segment straight: an empty profile is an import or a segment saved before
 * profiles were captured.
 */
export const PROFILE_LINE = 'line';

/** Which kind of hand-added cue an instruction is, if it is one at all. */
export type CueKind = 'turn' | 'note';

/** What GraphHopper returns for a turn, of which we keep four fields. */
export interface GraphhopperInstruction {
  text?: string;
  sign?: number;
  interval?: number[];
  street_name?: string;
}

/**
 * Distance along a segment at each of its coordinates.
 *
 * The one distance convention the planner uses: this is what goes into the geometry's M
 * value at save time *and* what positions the turns, so a turn and the geometry it belongs
 * to can never end up on different scales. The recording apps correct for that disagreement
 * when they find it; producing it in the first place is worse than correcting it.
 */
export function cumulativeDistancesMeters(coordinates: number[][]): number[] {
  if (!coordinates || coordinates.length === 0) return [];

  const distances: number[] = [0];

  for (let i = 1; i < coordinates.length; i++) {
    const previous = coordinates[i - 1];
    const current = coordinates[i];
    const segmentDistance = turfDistance(
      [previous[0], previous[1]],
      [current[0], current[1]],
      { units: 'meters' }
    );

    distances.push(Math.round((distances[i - 1] + segmentDistance) * 10) / 10);
  }

  return distances;
}

/**
 * The turns GraphHopper reported, positioned along the geometry it drew them for.
 *
 * `interval[0]` is where the maneuver happens — the first point of its interval, not the
 * last — and it indexes the coordinates exactly as GraphHopper returned them, so this must
 * be measured before anything de-duplicates or trims them. Duplicate points contribute no
 * length, so the distances still line up with the de-duplicated geometry that gets saved.
 *
 * Anything unreadable is dropped rather than guessed at: a missing turn is silence, a
 * mis-placed one is a confident wrong instruction.
 */
export function turnInstructionsFrom(
  instructions: GraphhopperInstruction[] | undefined,
  rawCoordinates: number[][]
): TurnInstruction[] {
  if (!instructions || instructions.length === 0) return [];
  if (!rawCoordinates || rawCoordinates.length < 2) return [];

  const distances = cumulativeDistancesMeters(rawCoordinates);

  return instructions.reduce<TurnInstruction[]>((kept, instruction) => {
    const index = instruction.interval?.[0];
    if (index === undefined || index < 0 || index >= distances.length) return kept;

    const street = instruction.street_name?.trim();
    const turn: TurnInstruction = {
      d: distances[index],
      sign: instruction.sign ?? 0,
      text: instruction.text ?? ''
    };
    if (street) turn.street = street;

    kept.push(turn);
    return kept;
  }, []);
}

/**
 * A segment's turns for walking it the other way.
 *
 * A port of `mirrored` on Android and iOS, rule for rule — a route reversed on one platform is
 * walked on another, so all three have to flip it identically:
 *
 *  - bike and car segments drop their turns, because a wheeled route reversed may not be
 *    travellable;
 *  - cues are dropped, because a note describes one direction of travel;
 *  - distances measure from the other end, on the segment's own stored scale;
 *  - left and right swap, keeping severity; continue and the bookends keep their sign;
 *  - a chosen direction (`usign`) flips, a person's wording (`utext`) is dropped, and a
 *    suppressed turn stays suppressed;
 *  - `text` is blanked so each app words the turn from its sign;
 *  - the street shifts by one: forward, a turn names the road you leave onto, but walking
 *    back you arrive along that road and leave onto the previous one.
 */
export function mirroredInstructions(
  turns: TurnInstruction[],
  profile: string | null | undefined,
  geometryLengthMeters: number
): TurnInstruction[] {
  if (!turns || turns.length === 0) return [];
  if (profile === 'bike' || profile === 'car') return [];

  const forward = turns.filter((turn) => turn.cue == null).sort((a, b) => a.d - b.d);
  if (forward.length === 0) return [];

  const captured = capturedLength(turns);
  const length = captured !== undefined && captured > 0 ? captured : geometryLengthMeters;
  const arrival = [...forward].reverse().find((turn) => isTerminal(turn.sign));
  const opening = forward[0] === arrival ? undefined : forward[0];
  const flipped = forward
    .map((turn, index) => {
      const mirrored: TurnInstruction = {
        // Unrounded, as on both phones, so a route reversed anywhere stores the same numbers.
        d: Math.max(0, length - turn.d),
        sign: mirroredSign(turn.sign),
        text: ''
      };
      const street = forward[index - 1]?.street;
      if (street) mirrored.street = street;
      if (turn.usign !== undefined) mirrored.usign = mirroredSign(turn.usign);
      if (turn.hidden) mirrored.hidden = true;
      if (turn.src && turn !== arrival && turn !== opening) mirrored.src = turn.src;
      // On a tie, the old arrival leads and the old opening trails, so each lands where it
      // becomes the new bookend. A turn sharing the arrival's distance — the junction turn the
      // spur trim writes — would otherwise take index 0, which every reader drops.
      const rank = turn === arrival ? -1 : turn === opening ? 1 : 0;
      return { mirrored, rank };
    })
    .sort((a, b) => a.mirrored.d - b.mirrored.d || a.rank - b.rank)
    .map(({ mirrored }) => mirrored);

  // Mirroring moves the arrival to the start and the opening to the end; put them back.
  if (isTerminal(flipped[0].sign)) flipped[0] = { ...flipped[0], sign: ManeuverSign.CONTINUE };
  const last = flipped.length - 1;
  if (flipped[last].sign === ManeuverSign.CONTINUE) {
    flipped[last] = { ...flipped[last], sign: ManeuverSign.FINISH };
  }
  return flipped;
}

/**
 * A segment's turns divided where a new marker cuts it in two — a port of `splitAt` on Android
 * and `PlannerSegmentInstructions.split` on iOS, so a route split anywhere reads the same.
 */
export function splitInstructions(
  turns: TurnInstruction[],
  firstLengthMeters: number,
  geometryLengthMeters: number
): [TurnInstruction[], TurnInstruction[]] {
  if (!turns || turns.length === 0) return [[], []];

  const ordered = sortedByDistance(turns);
  const captured = capturedLength(ordered);
  const total = captured ?? geometryLengthMeters;
  // Phone-captured turns run a few percent off the geometry, so the cut moves onto their scale.
  const scale = captured !== undefined && captured > 0 && geometryLengthMeters > 0
    ? captured / geometryLengthMeters
    : 1;
  const cut = round(firstLengthMeters * scale);
  if (cut <= 0 || cut >= total) return [[], []];

  const body = ordered.filter((turn) => !isTerminal(turn.sign));
  // A turn exactly on the cut is the junction the marker went on; it stays with the first half,
  // where the spur trim already puts a junction turn, so its wording and road are not lost.
  const upToCut = body.filter((turn) => turn.d <= cut);

  // The line still runs straight through the new marker, so GraphHopper's turns either side are
  // already the whole story there: both bookends at it say so.
  const routed = body.some((turn) => turn.cue == null);
  const arrival = arrivalAt(cut);
  const opening = openingOn(upToCut[upToCut.length - 1]?.street);
  if (routed) arrival.src = opening.src = JOIN_SOURCE;
  const first = [...upToCut, arrival];
  const end = arrivalAt(round(total - cut));
  const settledEnd = [...ordered].reverse().find((turn) => isTerminal(turn.sign))?.src;
  if (settledEnd) end.src = settledEnd;
  const second = [
    opening,
    ...body.filter((turn) => turn.d > cut).map((turn) => ({ ...turn, d: round(turn.d - cut) })),
    end
  ];

  return [first, second];
}

/**
 * The turns for one segment formed by laying `right` after `left`, when the marker between
 * them is deleted — a port of `PlannerSegmentInstructions.merged` on iOS.
 *
 * `junctionSign` is a turn the list only showed because the join turned there; once the join
 * is gone nothing would show it, so it is stored at the join.
 */
export function mergedInstructions(
  left: TurnInstruction[],
  leftGeometryMeters: number,
  right: TurnInstruction[],
  rightGeometryMeters: number,
  junctionSign?: number
): TurnInstruction[] {
  // Two straight lines stay a straight line, whatever the join did.
  if (!left?.length && !right?.length) return [];

  const body = (turns: TurnInstruction[]) => sortedByDistance(turns).filter((turn) => !isTerminal(turn.sign));
  const leftBody = body(left);
  const rightBody = body(right);
  const rightOpening =
    rightBody[0] && rightBody[0].sign === ManeuverSign.CONTINUE && rightBody[0].d <= 0 && rightBody[0].cue == null
      ? rightBody.shift()
      : undefined;
  const junction =
    junctionSign !== undefined && junctionSign !== ManeuverSign.CONTINUE ? junctionSign : undefined;

  // Each half on its own captured scale; a half with no arrival (a straight line) falls back to
  // its geometry, so the merged arrival still lands at the end the reader scales by.
  const leftLength = capturedLength(sortedByDistance(left)) ?? leftGeometryMeters;
  const rightLength = capturedLength(sortedByDistance(right)) ?? rightGeometryMeters;

  const turns: TurnInstruction[] = leftBody.length === 0 ? [openingOn(undefined)] : [];
  turns.push(...leftBody);
  if (junction !== undefined) {
    const turn: TurnInstruction = { d: round(leftLength), sign: junction, text: '' };
    if (rightOpening?.street) turn.street = rightOpening.street;
    turns.push(turn);
  }
  turns.push(...rightBody.map((turn) => ({ ...turn, d: round(turn.d + leftLength) })));
  const end = arrivalAt(round(leftLength + rightLength));
  const settledEnd = sortedByDistance(right).reverse().find((turn) => isTerminal(turn.sign))?.src;
  if (settledEnd) end.src = settledEnd;
  turns.push(end);
  return turns;
}

function sortedByDistance(turns: TurnInstruction[]): TurnInstruction[] {
  return [...(turns ?? [])].sort((a, b) => a.d - b.d);
}

function openingOn(street: string | undefined): TurnInstruction {
  const opening: TurnInstruction = { d: 0, sign: ManeuverSign.CONTINUE, text: '' };
  if (street) opening.street = street;
  return opening;
}

function arrivalAt(d: number): TurnInstruction {
  return { d, sign: ManeuverSign.FINISH, text: '' };
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function capturedLength(turns: TurnInstruction[]): number | undefined {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (isTerminal(turns[i].sign)) return turns[i].d;
  }
  return undefined;
}

function mirroredSign(sign: number): number {
  return sign === ManeuverSign.CONTINUE || isTerminal(sign) ? sign : -sign;
}
